/**
 * Custom user-defined actions for drawers (CRM Account, Ticket, Proposal, ...).
 *
 * Types supported:
 *   - open_url:    abre URL numa nova aba com placeholders substituidos.
 *   - send_email:  abre cliente de email (mailto:) com subject/body pre-preenchidos.
 *   - copy_text:   copia texto (com placeholders) para o clipboard.
 *   - webhook:     chama POST (por defeito) via proxy do backend para evitar CORS.
 *
 * Placeholders no formato {chave} onde chave e um campo devolvido por
 * getPlaceholders(entity) do drawer que invoca a acao.
 */
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";

export const ACTION_TYPES = [
  { value: "open_url", label: "Abrir URL externo" },
  { value: "send_email", label: "Enviar email (mailto)" },
  { value: "copy_text", label: "Copiar para clipboard" },
  { value: "webhook", label: "Chamar webhook (POST)" },
];

const ACTION_TYPE_LABELS = Object.fromEntries(ACTION_TYPES.map((t) => [t.value, t.label]));
export const actionTypeLabel = (t) => ACTION_TYPE_LABELS[t] || t;

export function newCustomAction() {
  return {
    id: (crypto.randomUUID && crypto.randomUUID()) || `ca_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    label: "Nova acao",
    icon: "Zap",
    type: "open_url",
    visible: true,
    config: {
      url: "",
      method: "POST",
      payload: "",
      headers: "",
      subject: "",
      body: "",
      text: "",
      email_to: "",
    },
  };
}

export function replacePlaceholders(template, placeholders) {
  if (!template) return "";
  return String(template).replace(/\{([a-zA-Z0-9_.-]+)\}/g, (_, key) => {
    const v = placeholders[key];
    return v == null ? "" : String(v);
  });
}

function tryParseJSON(str) {
  if (!str || !str.trim()) return null;
  try { return JSON.parse(str); } catch { return null; }
}

export async function runCustomAction(action, placeholders) {
  const cfg = action.config || {};
  const type = action.type;
  try {
    if (type === "open_url") {
      const url = replacePlaceholders(cfg.url, placeholders);
      if (!url) { toast.error("URL vazio na acao."); return; }
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (type === "send_email") {
      const to = replacePlaceholders(cfg.email_to || "{email}", placeholders);
      const subject = replacePlaceholders(cfg.subject || "", placeholders);
      const body = replacePlaceholders(cfg.body || "", placeholders);
      if (!to) { toast.error("Destinatario vazio (verifique placeholder {email})."); return; }
      const qs = [
        subject ? `subject=${encodeURIComponent(subject)}` : "",
        body ? `body=${encodeURIComponent(body)}` : "",
      ].filter(Boolean).join("&");
      window.location.href = `mailto:${to}${qs ? "?" + qs : ""}`;
      return;
    }
    if (type === "copy_text") {
      const text = replacePlaceholders(cfg.text || "", placeholders);
      if (!text) { toast.error("Texto vazio na acao."); return; }
      await navigator.clipboard.writeText(text);
      toast.success("Copiado para o clipboard.");
      return;
    }
    if (type === "webhook") {
      const url = replacePlaceholders(cfg.url, placeholders);
      if (!url) { toast.error("URL do webhook vazio."); return; }
      const method = (cfg.method || "POST").toUpperCase();
      const payloadRaw = replacePlaceholders(cfg.payload || "", placeholders);
      const headersRaw = replacePlaceholders(cfg.headers || "", placeholders);
      const parsedPayload = tryParseJSON(payloadRaw);
      const parsedHeaders = tryParseJSON(headersRaw) || {};
      const { data } = await api.post("/ui/webhook", {
        url, method,
        payload: parsedPayload !== null ? parsedPayload : (payloadRaw || null),
        headers: parsedHeaders,
      });
      const st = data?.status_code;
      if (st && st >= 200 && st < 300) toast.success(`Webhook OK (${st}).`);
      else toast.error(`Webhook falhou (${st || "sem resposta"}).`);
      return;
    }
    toast.error(`Tipo de acao desconhecido: ${type}`);
  } catch (e) {
    toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Falha a executar acao.");
  }
}
